/**
 * AI3D Fab-in-Place Types
 * Asset fabrication pipeline types for converting AI-generated 3D objects
 */

export type InputFormat = 'ply' | 'glb';

export type IntentProfile = 'web_realtime' | 'quest_mr' | 'showcase';

export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type ViewMode = 'raw' | 'fabricated' | 'split';

export interface Asset {
  asset_id: string;
  source: 'upload' | 'url';
  input_format: InputFormat;
  raw_url: string;
  created_at: string;
}

export interface FabricationPlan {
  profile: IntentProfile;
  target_triangles: number;
  texture_resolution: number;
  generate_normals: boolean;
  generate_ao: boolean;
  uv_method: 'smart_uv' | 'lightmap_pack';
}

export interface Job {
  job_id: string;
  asset_id: string;
  profile: IntentProfile;
  status: JobStatus;
  vultr_job_id?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error?: string;
}

export interface JobStats {
  job_id: string;
  input_tris: number;
  output_tris: number;
  input_bytes: number;
  output_bytes: number;
  texture_res: number;
  runtime_ms: number;
}

export interface FabricationResult {
  job: Job;
  stats: JobStats;
  output_url: string;
  preview_url: string;
}

export interface ProfileConfig {
  id: IntentProfile;
  name: string;
  description: string;
  target_triangles: number;
  texture_resolution: number;
  generate_normals: boolean;
  generate_ao: boolean;
}

export const FABRICATION_PROFILES: Record<IntentProfile, ProfileConfig> = {
  web_realtime: {
    id: 'web_realtime',
    name: 'WebGL Realtime',
    description: 'Optimized for web browsers and real-time rendering',
    target_triangles: 10000,
    texture_resolution: 1024,
    generate_normals: true,
    generate_ao: false,
  },
  quest_mr: {
    id: 'quest_mr',
    name: 'Quest / MR',
    description: 'Optimized for Meta Quest and mixed reality devices',
    target_triangles: 5000,
    texture_resolution: 512,
    generate_normals: true,
    generate_ao: false,
  },
  showcase: {
    id: 'showcase',
    name: 'Showcase',
    description: 'High quality for portfolio and presentations',
    target_triangles: 50000,
    texture_resolution: 2048,
    generate_normals: true,
    generate_ao: true,
  },
};
